// 🧱🗂️ Rooms by purpose: the room FRAME a purpose opens with
// (SPACES_REDESIGN_BRAINSTORM_2026_09_29 §5.2-§5.5a; the approved mockup
// rooms-by-purpose.html, frame A "Center stream + snap rails", presets a + b).
//
// Nina 09-29 night (§9-bis, ruling on #17): the new frame starts with NEW
// purposes only, Workstation first, behind a hidden switch. So this registry
// holds exactly two presets:
//   learn     Productivity & learning  → "Workstation"
//   research  Research & academia      → "Research"
// Every other purpose (and every room with none: all of today's rooms) gets
// null here and keeps today's page, byte for byte.
//
// ON since 10-01 (Nina: "Right after" the unlisted leak fixes), with build
// form v2 (components/buildForm/buildFormSwitch.js) in the same edit. Two keys
// still decide it:
//   1. the room's data.purpose: only build form v2 stamps one. The status
//      payload echoes it beside `space_goal` (chatGameApi getGameStatus:
//      `purpose` + `purpose_kind`, re-checked through spacePurpose, null for
//      every room made before the form). No purpose = today's page;
//   2. SHOW_ROOM_SHELL below (the SHOW_PORTED_PLACE / buildFormSwitch pattern:
//      an exported constant, test-pinned, so flipping it is a deliberate
//      two-file edit). The admin preview (a browser holding the /admin key:
//      ?shell=1 remembers it on that device, ?shell=0 forgets it) changes
//      nothing on screen while the switch is on. It stays so that turning the
//      switch back off is the whole rollback, with the one-device preview.
//
// A preset is a DECLARATION the mount wave reads; nothing here renders.
//   name           the purpose tag in the top bar
//   accent         the room's ONE accent (mockup: Workstation blue, Research
//                  green); the frame's chrome is C's neutral tokens around it
//   entry 'none'   no host intro, no "meet my locals" deal-in
//   art 'thin'     the thin top bar, no banner art
//   host false     no moderator (the room's own key, never home_agent_id)
//   ghost 'off'    no side chat lane
//   stage 'none'   nothing on the stage at open (the PDF / screen stage tools
//                  are later waves: awareness without ability is a trap)
//   music 'menu'   the app's ♫ button leaves the corner; ⋯ → Music opens it
//                  (LofiRadio's own MUSIC_FAB_HIDDEN / MUSIC_OPEN events)
//   panels         the rails a first visit opens with (utils/roomPanels ids).
//                  ONLY panels that really exist: Files · Notes · People ·
//                  Export. The mockup's Room library, Tasks, Papers and
//                  Citations are not built, so they are not here.
//   composerTools  composer extras by id (COMPOSER_TOOLS below)
//
// Pure: no React, no DOM beyond the guarded preview read.

// ── the switch ──────────────────────────────────────────────────────────────
export const SHOW_ROOM_SHELL = true; // ON since 10-01 (Nina: "Right after" the unlisted leak fixes)
export const ROOM_SHELL_PREVIEW_KEY = 'room_shell_preview';
export const ROOM_SHELL_PARAM = 'shell';

export function roomShellPreviewOn(win = (typeof window !== 'undefined' ? window : null)) {
  try {
    const ls = win && win.localStorage;
    if (!ls || !ls.getItem('adminApiKey')) return false;
    const q = new URLSearchParams(win.location.search).get(ROOM_SHELL_PARAM);
    if (q === '1') ls.setItem(ROOM_SHELL_PREVIEW_KEY, '1');
    if (q === '0') ls.removeItem(ROOM_SHELL_PREVIEW_KEY);
    return ls.getItem(ROOM_SHELL_PREVIEW_KEY) === '1';
  } catch (e) { return false; }
}

// The preview runs FIRST, so ?shell=0 still forgets a stored preview with the
// switch on (a bare `SHOW || preview` would short-circuit and never read it).
export const roomShellOn = (win) => roomShellPreviewOn(win) || SHOW_ROOM_SHELL;

// ── composer extras ─────────────────────────────────────────────────────────
// 'share' = the composer's EXISTING 📎 upload (HumanInputForm onUploadFile:
// a doc, an image or a video, 7 MB), surfaced as a labeled "Share" button.
// Screen share is LivestreamPanel's and desktop only; it joins with the stage.
export const COMPOSER_TOOLS = Object.freeze({
  share: Object.freeze({ id: 'share', label: 'Share', hint: 'A doc, an image or a video' }),
});

const freezeDeep = (o) => {
  Object.values(o).forEach((v) => { if (v && typeof v === 'object') freezeDeep(v); });
  return Object.freeze(o);
};

// ── the presets ─────────────────────────────────────────────────────────────
const QUIET = { entry: 'none', art: 'thin', host: false, ghost: 'off', stage: 'none', music: 'menu' };

export const ROOM_PRESETS = freezeDeep({
  learn: {
    purpose: 'learn', name: 'Workstation', accent: '#4F8CFF', ...QUIET,
    panels: { left: ['files'], right: ['notes'] },
    composerTools: ['share'],
  },
  research: {
    purpose: 'research', name: 'Research', accent: '#34C38F', ...QUIET,
    panels: { left: ['files'], right: ['notes'] },
    composerTools: ['share'],
  },
});

export const SHELL_PURPOSES = Object.freeze(Object.keys(ROOM_PRESETS));

/** The frame preset for a purpose key: a fresh copy, or null (today's page). */
export function presetFor(purpose) {
  if (typeof purpose !== 'string' || !Object.prototype.hasOwnProperty.call(ROOM_PRESETS, purpose)) return null;
  const p = ROOM_PRESETS[purpose];
  return {
    ...p,
    panels: { left: [...p.panels.left], right: [...p.panels.right] },
    composerTools: [...p.composerTools],
  };
}

/**
 * The ONE gate the mount wave asks: which preset (if any) does THIS room open
 * with? null = today's page, byte for byte.
 *   status  the room's status payload (its `purpose` key, once echoed)
 *   on      roomShellOn(window): the switch or this device's preview
 *   bot     ?bot=1 (the explorer fleet reads today's DOM)
 *   gameType  the page's gameType: anything but a chat game ('chat-game' /
 *           'chat', or absent) keeps its page (Big Brother, BOTC: spec §5.3)
 * Never for competitive / imposter rooms (their ghost chat is SECRET), a
 * ported 1:1 room (Their Place is its own frame) or the House (its rooms and
 * camera shell), whatever purpose they carry.
 */
const CHAT_GAME_TYPES = Object.freeze(['chat-game', 'chat']);

export function shellPresetFor(status, { on = false, bot = false, gameType = null } = {}) {
  if (!on || bot || !status || typeof status !== 'object') return null;
  if (gameType != null && !CHAT_GAME_TYPES.includes(gameType)) return null;
  if (status.competitive_mode || status.imposter_mode || status.is_ported || status.house) return null;
  return presetFor(status.purpose);
}
